import { useTheme } from '../context/ThemeContext';
import { useState, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { updateHousehold, addIncome, addCategory, createLinkToken, exchangeToken, syncPlaid } from '../api/client';


const getInpStyle = (COLORS) => ({
  width: '100%', padding: '10px 12px', background: COLORS.bg,
  border: `1px solid ${COLORS.border}`, borderRadius: 10,
  color: COLORS.text, fontSize: 14, fontFamily: 'inherit',
  boxSizing: 'border-box', outline: 'none',
});

const STEPS = [
  { id: 'household', title: 'Name Your Household', emoji: '🏠' },
  { id: 'period',    title: 'Budget Period',        emoji: '📅' },
  { id: 'income',    title: 'Income Sources',       emoji: '💰' },
  { id: 'categories',title: 'Budget Categories',   emoji: '📊' },
  { id: 'bank',      title: 'Connect Your Bank',   emoji: '🏦' },
];

const WEEK_DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

// ─── Plaid Link Button ────────────────────────────────────────────────────────
const PlaidLinkButton = ({ onSuccess }) => {
  const { COLORS } = useTheme();
  const [loadingToken, setLoadingToken] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [linked, setLinked] = useState(false);

  const fetchToken = async () => {
    setLoadingToken(true);
    try {
      const res = await createLinkToken();
      const token = res.data?.link_token || res.data?.data?.link_token;
      if (token) {
        setLinkToken(token);
        // So Fidelity OAuth can resume after redirect to /plaid/oauth
        localStorage.setItem('ledger_plaid_link_token', token);
      }
      return token;
    } catch (e) { console.error(e); return null; }
    finally { setLoadingToken(false); }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token) => {
      localStorage.removeItem('ledger_plaid_link_token');
      await exchangeToken(public_token);
      await syncPlaid();
      setLinked(true);
      onSuccess?.();
    },
    onExit: () => {},
  });

  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  if (linked) return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
      <div style={{ color: COLORS.green, fontWeight: 700, fontSize: 15 }}>Bank connected!</div>
      <div style={{ color: COLORS.textMuted, fontSize: 13, marginTop: 4 }}>Your accounts are syncing.</div>
    </div>
  );

  return (
    <button
      onClick={() => { if (!linkToken) fetchToken(); else if (ready) open(); }}
      disabled={loadingToken || (linkToken && !ready)}
      style={{ width: '100%', padding: '14px', background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}66`, borderRadius: 12, color: COLORS.accent, fontFamily: 'inherit', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
      {loadingToken ? 'Preparing...' : '+ Connect a Bank Account'}
    </button>
  );
};

// ─── Main Onboarding Component ────────────────────────────────────────────────
export default function Onboarding({ householdName: initialName, onComplete }) {
  const { COLORS } = useTheme();
  const inp = getInpStyle(COLORS);
  const sel = inp;
  const [step, setStep]               = useState(0);
  const [saving, setSaving]           = useState(false);
  const [joining, setJoining]         = useState(false); // "joining existing household" mode

  // Step 1 — Household
  const [hhName, setHhName]           = useState(initialName || '');

  // Step 2 — Period
  const [monthStart, setMonthStart]   = useState('26');
  const [weekStart, setWeekStart]     = useState('Thursday');

  // Step 3 — Income
  const [incomes, setIncomes]         = useState([{ name: '', amount: '', schedule_type: 'monthly', month_day: '1', week_day: 'Friday', month_day_1: '1', month_day_2: '15' }]);

  // Step 4 — Categories
  const [categories, setCategories]   = useState([
    { name: 'Rent / Mortgage', amount: '', type: 'fixed',    due_day: '1' },
    { name: 'Groceries',       amount: '', type: 'variable', due_day: '' },
  ]);

  // Step 5 — Bank
  const [bankLinked, setBankLinked]   = useState(false);

  const currentStep = STEPS[step];

  const addIncomeLine = () => setIncomes(prev => [...prev, { name: '', amount: '', schedule_type: 'monthly', month_day: '1', week_day: 'Friday', month_day_1: '1', month_day_2: '15' }]);
  const removeIncomeLine = (i) => setIncomes(prev => prev.filter((_, idx) => idx !== i));
  const updateIncomeLine = (i, field, val) => setIncomes(prev => prev.map((inc, idx) => idx === i ? { ...inc, [field]: val } : inc));

  const addCategoryLine = () => setCategories(prev => [...prev, { name: '', amount: '', type: 'variable', due_day: '' }]);
  const removeCategoryLine = (i) => setCategories(prev => prev.filter((_, idx) => idx !== i));
  const updateCategoryLine = (i, field, val) => setCategories(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: val } : c));

  const handleNext = async () => {
    setSaving(true);
    try {
      if (step === 0) {
        await updateHousehold({ name: hhName });
      } else if (step === 1) {
        await updateHousehold({ month_start: parseInt(monthStart), week_start: weekStart.toLowerCase() });
      } else if (step === 2) {
        for (const inc of incomes) {
          if (!inc.name || !inc.amount) continue;
          const payload = { name: inc.name, amount: parseFloat(inc.amount), schedule_type: inc.schedule_type };
          if (inc.schedule_type === 'monthly')      payload.month_day = parseInt(inc.month_day);
          if (inc.schedule_type === 'weekly')       payload.week_day  = inc.week_day;
          if (inc.schedule_type === 'biweekly')     payload.week_day  = inc.week_day;
          if (inc.schedule_type === 'twiceMonthly') { payload.month_day_1 = parseInt(inc.month_day_1); payload.month_day_2 = parseInt(inc.month_day_2); }
          await addIncome(payload);
        }
      } else if (step === 3) {
        for (const cat of categories) {
          if (!cat.name || !cat.amount) continue;
          const payload = { name: cat.name, amount: parseFloat(cat.amount), type: cat.type };
          if (cat.type === 'fixed' && cat.due_day) payload.due_day = parseInt(cat.due_day);
          await addCategory(payload);
        }
      } else if (step === 4) {
        await updateHousehold({ onboarding_complete: true });
        onComplete?.();
        return;
      }
      setStep(s => s + 1);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleSkip = async () => {
    if (step === 4) {
      await updateHousehold({ onboarding_complete: true });
      onComplete?.();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      await updateHousehold({ onboarding_complete: true });
      onComplete?.();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (joining) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: COLORS.bg, zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: '0 0 12px', textAlign: 'center' }}>Waiting for invite</h2>
        <p style={{ color: COLORS.textMuted, fontSize: 14, textAlign: 'center', lineHeight: 1.6, margin: '0 0 32px' }}>
          Ask the household owner to invite your email from their Settings → Team tab. Once they do, log out and back in and you'll be all set.
        </p>
        <button onClick={() => setJoining(false)} style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>
          Go back
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: COLORS.bg, zIndex: 1000, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

      {/* Progress bar */}
      <div style={{ height: 3, background: COLORS.border }}>
        <div style={{ height: '100%', background: COLORS.accent, width: `${((step + 1) / STEPS.length) * 100}%`, transition: 'width 0.3s ease' }} />
      </div>

      {/* Step indicators */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '20px 24px 0' }}>
        {STEPS.map((s, i) => (
          <div key={s.id} style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, background: i < step ? COLORS.green : i === step ? COLORS.accent : COLORS.surface, color: i <= step ? '#fff' : COLORS.textMuted, border: `2px solid ${i < step ? COLORS.green : i === step ? COLORS.accent : COLORS.border}`, flexShrink: 0, transition: 'all 0.3s' }}>
            {i < step ? '✓' : i + 1}
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ textAlign: 'center', padding: '24px 24px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>{currentStep.emoji}</div>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0 }}>{currentStep.title}</h2>
        <p style={{ color: COLORS.textMuted, fontSize: 14, marginTop: 6 }}>Step {step + 1} of {STEPS.length}</p>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '24px', maxWidth: 480, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

        {/* ── Step 1: Household Name ── */}
        {step === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ color: COLORS.textMuted, fontSize: 14, margin: 0 }}>What should we call your household budget?</p>
            <input
              style={inp}
              placeholder="e.g. Schirmer Family Budget"
              value={hhName}
              onChange={e => setHhName(e.target.value)}
            />
            <button onClick={() => setJoining(true)} style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', padding: 0, textAlign: 'left', fontFamily: 'inherit' }}>
              Joining someone else's household instead?
            </button>
          </div>
        )}

        {/* ── Step 2: Budget Period ── */}
        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <label style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Monthly Budget Start Day</label>
              <p style={{ color: COLORS.textMuted, fontSize: 13, margin: '0 0 10px' }}>Your budget period resets on this day each month (e.g. 26 = 26th to 25th).</p>
              <select style={sel} value={monthStart} onChange={e => setMonthStart(e.target.value)}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of the month</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ color: COLORS.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Weekly Budget Start Day</label>
              <p style={{ color: COLORS.textMuted, fontSize: 13, margin: '0 0 10px' }}>Each week in your Weekly view starts on this day.</p>
              <select style={sel} value={weekStart} onChange={e => setWeekStart(e.target.value)}>
                {WEEK_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* ── Step 3: Income ── */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ color: COLORS.textMuted, fontSize: 14, margin: 0 }}>Add your income sources. You can add more later in Settings.</p>
            {incomes.map((inc, i) => (
              <div key={i} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inp, flex: 2 }} placeholder="Name (e.g. Paycheck)" value={inc.name} onChange={e => updateIncomeLine(i, 'name', e.target.value)} />
                  <input style={{ ...inp, flex: 1 }} placeholder="Amount" type="number" value={inc.amount} onChange={e => updateIncomeLine(i, 'amount', e.target.value)} />
                </div>
                <select style={sel} value={inc.schedule_type} onChange={e => updateIncomeLine(i, 'schedule_type', e.target.value)}>
                  <option value="monthly">Monthly</option>
                  <option value="twiceMonthly">Twice Monthly</option>
                  <option value="biweekly">Every Two Weeks</option>
                  <option value="weekly">Weekly</option>
                </select>
                {inc.schedule_type === 'monthly' && (
                  <input style={inp} placeholder="Day of month (e.g. 1)" type="number" min="1" max="31" value={inc.month_day} onChange={e => updateIncomeLine(i, 'month_day', e.target.value)} />
                )}
                {(inc.schedule_type === 'weekly' || inc.schedule_type === 'biweekly') && (
                  <select style={sel} value={inc.week_day} onChange={e => updateIncomeLine(i, 'week_day', e.target.value)}>
                    {WEEK_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                )}
                {inc.schedule_type === 'twiceMonthly' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input style={{ ...inp, flex: 1 }} placeholder="1st day" type="number" min="1" max="31" value={inc.month_day_1} onChange={e => updateIncomeLine(i, 'month_day_1', e.target.value)} />
                    <input style={{ ...inp, flex: 1 }} placeholder="2nd day" type="number" min="1" max="31" value={inc.month_day_2} onChange={e => updateIncomeLine(i, 'month_day_2', e.target.value)} />
                  </div>
                )}
                {incomes.length > 1 && (
                  <button onClick={() => removeIncomeLine(i)} style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: COLORS.red, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Remove</button>
                )}
              </div>
            ))}
            <button onClick={addIncomeLine} style={{ padding: '10px', background: 'none', border: `1px dashed ${COLORS.border}`, borderRadius: 10, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              + Add another income source
            </button>
          </div>
        )}

        {/* ── Step 4: Categories ── */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ color: COLORS.textMuted, fontSize: 14, margin: 0 }}>Set up your budget categories. Fixed bills have a due date, variable categories track monthly spending.</p>
            {categories.map((cat, i) => (
              <div key={i} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...inp, flex: 2 }} placeholder="Category name" value={cat.name} onChange={e => updateCategoryLine(i, 'name', e.target.value)} />
                  <input style={{ ...inp, flex: 1 }} placeholder="Budget $" type="number" value={cat.amount} onChange={e => updateCategoryLine(i, 'amount', e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select style={{ ...sel, flex: 1 }} value={cat.type} onChange={e => updateCategoryLine(i, 'type', e.target.value)}>
                    <option value="fixed">Fixed Bill</option>
                    <option value="variable">Variable</option>
                    <option value="misc">Misc</option>
                  </select>
                  {cat.type === 'fixed' && (
                    <input style={{ ...inp, flex: 1 }} placeholder="Due day" type="number" min="1" max="31" value={cat.due_day} onChange={e => updateCategoryLine(i, 'due_day', e.target.value)} />
                  )}
                </div>
                {categories.length > 1 && (
                  <button onClick={() => removeCategoryLine(i)} style={{ alignSelf: 'flex-end', background: 'none', border: 'none', color: COLORS.red, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Remove</button>
                )}
              </div>
            ))}
            <button onClick={addCategoryLine} style={{ padding: '10px', background: 'none', border: `1px dashed ${COLORS.border}`, borderRadius: 10, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              + Add another category
            </button>
          </div>
        )}

        {/* ── Step 5: Bank ── */}
        {step === 4 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ color: COLORS.textMuted, fontSize: 14, margin: 0 }}>Connect your bank to automatically import transactions and track balances. You can skip this and connect later in Settings.</p>
            <PlaidLinkButton onSuccess={() => setBankLinked(true)} />
            {bankLinked && (
              <p style={{ color: COLORS.green, fontSize: 13, textAlign: 'center', margin: 0 }}>✓ Connected — your transactions will sync shortly.</p>
            )}
          </div>
        )}

      </div>

      {/* Footer buttons */}
      <div style={{ padding: '16px 24px 32px', maxWidth: 480, margin: '0 auto', width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button
          onClick={step === 4 ? handleFinish : handleNext}
          disabled={saving}
          style={{ width: '100%', padding: '14px', background: COLORS.accent, border: 'none', borderRadius: 12, color: '#fff', fontFamily: 'inherit', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving...' : step === 4 ? "Let's Go 🚀" : 'Continue →'}
        </button>
        <button
          onClick={handleSkip}
          style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: COLORS.textMuted, fontFamily: 'inherit', fontSize: 13, cursor: 'pointer' }}>
          {step === 4 ? 'Skip for now' : 'Skip this step'}
        </button>
        {step > 0 && (
          <button onClick={() => setStep(s => s - 1)} style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: COLORS.textMuted, fontFamily: 'inherit', fontSize: 13, cursor: 'pointer' }}>
            ← Back
          </button>
        )}
      </div>

    </div>
  );
}
